import React, { useCallback, useState, useEffect } from 'react';
import Joyride, { STATUS } from 'react-joyride';
import { FormattedMessage } from 'react-intl';
import BaseModal from 'components/Modals/BaseModal';
import PublishedModal from 'components/Modals/PublishedModal';
import Loading from 'components/Loading';
import ErrorMessage from 'components/ErrorMessage/ErrorMessage';
import { buildPublishedUrl } from 'helpers';
import { useTemplate, useToggleTypes } from 'hooks';
import { useDispatch, useSelector } from 'react-redux';
import { principalIdSelector } from 'store/selectors';
import { getCustomBlocksGroup } from 'store/actions';
import { USER_GUIDE_KEYS } from 'appConstants';
import cx from 'classnames';
import Grapes from './Grapes';
import { EditorContext } from './EditorContext';
import './Editor.sass';

const editorTourSteps = [
  {
    placement: 'center',
    target: 'body',
    title: <FormattedMessage id="editor-tour.start-step.title" />,
    content: <FormattedMessage id="editor-tour.start-step.message" />,
    locale: {
      skip: <FormattedMessage id="editor-tour.button.skip" />,
      next: <FormattedMessage id="editor-tour.button.start-tour" />,
    },
  },
  {
    placement: 'left-start',
    target: '.editor-content__canvas',
    styles: { options: { width: 270 } },
    title: <FormattedMessage id="editor-tour.canvas-step.title" />,
    content: (
      <div style={{ textAlign: 'justify' }}>
        <FormattedMessage id="editor-tour.canvas-step.message" />
      </div>
    ),
  },
  {
    placement: 'bottom',
    target: '.editor-header__panel-template-options',
    title: <FormattedMessage id="editor-tour.settings-step.title" />,
    content: (
      <div style={{ textAlign: 'justify' }}>
        <FormattedMessage id="editor-tour.settings-step.message" />
      </div>
    ),
  },
  {
    placement: 'right-start',
    target: '.editor-content__blocks',
    title: <FormattedMessage id="editor-tour.components-step.title" />,
    content: (
      <div style={{ textAlign: 'justify' }}>
        <FormattedMessage id="editor-tour.components-step.message" />
      </div>
    ),
  },
  {
    placement: 'left-start',
    target: '.editor-content__traits',
    title: <FormattedMessage id="editor-tour.traits-step.title" />,
    content: (
      <div style={{ textAlign: 'justify' }}>
        <FormattedMessage id="editor-tour.traits-step.message" />
      </div>
    ),
  },
  {
    placement: 'bottom',
    target: '.editor-content__panel-canvas-history',
    title: <FormattedMessage id="editor-tour.history-step.title" />,
    content: <FormattedMessage id="editor-tour.history-step.message" />,
  },
  {
    placement: 'bottom',
    target: '.editor-content__panel-responsive-actions',
    title: <FormattedMessage id="editor-tour.responsive-actions-step.title" />,
    content: (
      <FormattedMessage id="editor-tour.responsive-actions-step.message" />
    ),
  },
  {
    placement: 'bottom',
    target: '.editor-content__panel-canvas-controls',
    title: <FormattedMessage id="editor-tour.canvas-controls-step.title" />,
    content: <FormattedMessage id="editor-tour.canvas-controls-step.message" />,
  },
  {
    placement: 'center',
    target: 'body',
    title: <FormattedMessage id="editor-tour.final-step.title" />,
    content: (
      <FormattedMessage
        id="editor-tour.final-step.message"
        values={{
          link: (
            <a
              href="https://pagebuilder.guides.waymore.io/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#629AC7' }}>
              <FormattedMessage id="editor-tour.final-step.guide" />
            </a>
          ),
        }}
      />
    ),
    locale: {
      skip: <FormattedMessage id="editor-tour.button.skip" />,
      next: <FormattedMessage id="editor-tour.button.start-tour" />,
    },
  },
];

const Editor = () => {
  const {
    template,
    appearanceSettings,
    haveChanges,
    show,
    isError,
    isLoading,
    isSaveSettingsLoading,
    isUpdateLoading,
    isStatusLoading,
    isConvertLoading,
    enableCustomDomain,
    isConfirmModalOpen,
    isUpdateDomainLoading,
    isDeleteDomainLoading,
    isChangeInfoLoading,
    handleChangeTemplate,
    handleChangeInfo,
    handleChangeStatus,
    handleUploadImage,
    handleSaveSettings,
    handleSaveTemplate,
    handleConvertToSystem,
    handleShow,
    handleChangeAppearanceSettings,
    handleAddFont,
    handleGetDomain,
    handleVerifyDomain,
    handleChangeCustomDomain,
    handleToggleConfirmModal,
    handleActivateDomain,
    handleDeactivateDomain,
    handleDeleteDomain,
  } = useTemplate();

  const dispatch = useDispatch();

  const currentUser = useSelector(principalIdSelector);
  const [cbLoaded, setcbLoaded] = useState(false);
  const [grapesLoaded, setGrapesLoaded] = useState(false);
  const [isEditorTourFinished, setIsEditorTourFinished] = useState(() => {
    const initialIsEditorTourFinished = localStorage.getItem(
      USER_GUIDE_KEYS.EDITOR_TOUR,
    );

    // local storage stores only strings, so we should convert it by hand
    return initialIsEditorTourFinished === 'true';
  });
  const templateIsSharedWithCurrentUser =
    template?.isShared &&
    template?.userId !== currentUser &&
    template?.userId !== template?.collaboratorId &&
    !template?.collaboratorsList.includes(template?.userId);

  useEffect(() => {
    const requestCustomBlocksGroup = async () => {
      await dispatch(getCustomBlocksGroup());
      setcbLoaded(true);
    };

    requestCustomBlocksGroup();
  }, [dispatch]);

  const handleFinishEditorTour = ({ status }) => {
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      setIsEditorTourFinished(true);
      localStorage.setItem(USER_GUIDE_KEYS.EDITOR_TOUR, true);
    }
  };

  const handleRestartEditorTour = useCallback(() => {
    setIsEditorTourFinished(false);
    localStorage.removeItem(USER_GUIDE_KEYS.EDITOR_TOUR);
  }, []);

  const onLoad = useCallback(() => {
    setGrapesLoaded(true);
  }, []);

  if (isError) {
    return <ErrorMessage />;
  }

  if (isLoading) {
    return <Loading />;
  }

  if (!template) {
    return null;
  }

  return (
    <EditorContext.Provider
      value={{
        template,
        appearanceSettings,
        haveChanges,
        show,
        isSaveSettingsLoading,
        isUpdateLoading,
        isStatusLoading,
        isConvertLoading,
        enableCustomDomain,
        isConfirmModalOpen,
        isUpdateDomainLoading,
        isDeleteDomainLoading,
        isChangeInfoLoading,
        onChangeTemplate: handleChangeTemplate,
        onChangeInfo: handleChangeInfo,
        onChangeStatus: handleChangeStatus,
        onUploadImage: handleUploadImage,
        onSaveSettings: handleSaveSettings,
        onSaveTemplate: handleSaveTemplate,
        onConvertToSystem: handleConvertToSystem,
        onShow: handleShow,
        onChangeAppearanceSettings: handleChangeAppearanceSettings,
        onAddFont: handleAddFont,
        onRestartEditorTour: handleRestartEditorTour,
        handleGetDomain,
        handleVerifyDomain,
        handleChangeCustomDomain,
        handleToggleConfirmModal,
        handleActivateDomain,
        handleDeactivateDomain,
        handleDeleteDomain,
      }}>
      <div
        id="editor"
        className={cx({
          'shared-temp-current-user': templateIsSharedWithCurrentUser,
        })}>
        {cbLoaded && <Grapes onLoad={onLoad} />}
        {!grapesLoaded && <Loading />}
        <BaseModal
          isOpen={show.publishedModal}
          maxWidth="600px"
          headerTitle="published-modal.header-title"
          iconTitleClass="icon icon-check"
          headerClass="published-modal__header"
          onClose={() => handleShow(useToggleTypes.showPublishedModal)}>
          <PublishedModal
            isOpen={show.publishedModal}
            publishedUrl={buildPublishedUrl(template.id, template.status)}
            onClose={() => handleShow(useToggleTypes.showPublishedModal)}
          />
        </BaseModal>
      </div>
      {grapesLoaded && (
        <Joyride
          run={!isEditorTourFinished}
          steps={editorTourSteps}
          showSkipButton
          continuous
          callback={handleFinishEditorTour}
          styles={{
            options: {
              primaryColor: '#629AC7',
              textColor: '#110349',
              zIndex: 99,
            },
          }}
          locale={{
            skip: <FormattedMessage id="button.cancel" />,
            next: <FormattedMessage id="button.continue" />,
            back: <FormattedMessage id="button.back" />,
            last: <FormattedMessage id="button.finish" />,
          }}
        />
      )}
    </EditorContext.Provider>
  );
};

export default Editor;
